import React, { useState, useEffect } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Typography, CircularProgress, Button, IconButton, Box, Grid,
    FormControl, InputLabel, Select, MenuItem, TextField, Modal
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useAuthenticator } from '@aws-amplify/ui-react';

// Estilo para el modal
const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
};

const HolidayManagement = () => {
    const { user } = useAuthenticator((context) => [context.user]);

    // Estado para los filtros
    const [countries, setCountries] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState('');
    const [startYear, setStartYear] = useState(new Date().getFullYear().toString());
    const [endYear, setEndYear] = useState(new Date().getFullYear().toString());

    // Estado para los datos y la UI
    const [holidays, setHolidays] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentHoliday, setCurrentHoliday] = useState({
        date: '', name_es: '', name_en: '', type: 'RELIGIOUS', countryCode: ''
    });

    const apiEndpoint = Amplify.getConfig().API.REST.dnaAdminApi.endpoint;

    // --- LÓGICA DE API CON FETCH MANUAL ---

    // Función reutilizable para hacer llamadas a la API con el token de sesión
    const makeApiCall = async (method, path, body = null) => {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();
        if (!idToken) throw new Error('No se encontró el token de autenticación.');

        const headers = {
            'Content-Type': 'application/json',
            'Authorization': idToken
        };

        const config = { method, headers };
        if (body) {
            config.body = JSON.stringify(body);
        }

        const response = await fetch(`${apiEndpoint}${path}`, config);
        if (!response.ok) {
            const errorBody = await response.text();
            throw new Error(`Error de red: ${response.status} ${response.statusText} - ${errorBody}`);
        }

        const responseJson = await response.json();
        if (!responseJson.success) throw new Error(responseJson.msg || 'Error en la respuesta de la API');

        return responseJson.data;
    };

    // Cargar países para el dropdown al iniciar
    useEffect(() => {
        if (user) {
            const fetchCountries = async () => {
                try {
                    const data = await makeApiCall('GET', '/admin/countries');
                    setCountries(data);
                } catch (err) {
                    console.error('Error fetching countries:', err);
                    setError('No se pudieron cargar los países.');
                }
            };
            fetchCountries();
        }
    }, [user]);

    // Función para buscar festivos
    const handleSearch = async () => {
        if (!selectedCountry) {
            alert('Por favor, selecciona un país.');
            return;
        }
        try {
            setLoading(true);
            setError(null);
            const path = `/admin/holidays/search/${selectedCountry}?startYear=${startYear}&endYear=${endYear}`;
            const data = await makeApiCall('GET', path);
            setHolidays(data.sort((a, b) => a.date.localeCompare(b.date)));
        } catch (err) {
            console.error('Error fetching holidays:', err);
            setError('No se pudieron cargar los festivos.');
        } finally {
            setLoading(false);
        }
    };

    // Función para guardar (crear o actualizar) un festivo
    const handleSave = async () => {
        const { date, name_es, name_en, type, countryCode } = currentHoliday;
        if (!date || !name_es || !countryCode) {
            alert('Los campos País, Fecha y Nombre (ES) son obligatorios.');
            return;
        }
        const year = date.split('-')[0];
        const countryYear = `${countryCode}#${year}`;
        const payload = {
            countryYear,
            date,
            name: { es: name_es, en: name_en },
            type,
            countryCode
        };
        try {
            if (isEditing) {
                const path = `/admin/holidays/${encodeURIComponent(countryYear)}/${date}`;
                await makeApiCall('PUT', path, payload);
            } else {
                await makeApiCall('POST', '/admin/holidays', payload);
            }
            closeModalAndRefresh();
        } catch (err) {
            console.error('Error al guardar el festivo:', err);
            setError('No se pudo guardar el festivo.');
        }
    };

    // Función para eliminar un festivo
    const handleDelete = async (holiday) => {
        if (window.confirm(`¿Seguro que quieres eliminar el festivo del ${holiday.date}?`)) {
            try {
                const path = `/admin/holidays/${encodeURIComponent(holiday.countryYear)}/${holiday.date}`;
                await makeApiCall('DELETE', path);
                handleSearch();
            } catch (err){
                console.error('Error al eliminar el festivo:', err);
                setError('No se pudo eliminar el festivo.');
            }
        }
    };

    // --- MANEJO DEL MODAL Y FORMULARIO ---
    const handleFormChange = (e) => setCurrentHoliday(prev => ({ ...prev, [e.target.name]: e.target.value }));

    const openCreateModal = () => {
        setIsEditing(false);
        setCurrentHoliday({ date: '', name_es: '', name_en: '', type: 'RELIGIOUS', countryCode: '' });
        setIsModalOpen(true);
    };

    const openEditModal = (holiday) => {
        setIsEditing(true);
        setCurrentHoliday({
            date: holiday.date,
            name_es: holiday.name.es,
            name_en: holiday.name.en,
            type: holiday.type,
            countryCode: holiday.countryCode,
        });
        setIsModalOpen(true);
    };

    const closeModalAndRefresh = () => {
        setIsModalOpen(false);
        if(selectedCountry) handleSearch();
    };

    return (
        <Box>
            <Typography variant="h5" sx={{ mb: 2 }}>Gestión de Festivos</Typography>

            <Paper sx={{ p: 2, mb: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>País</InputLabel>
                            <Select value={selectedCountry} label="País" onChange={(e) => setSelectedCountry(e.target.value)}>
                                <MenuItem value="" disabled>
                                    <em>Selecciona un país</em>
                                </MenuItem>
                                {countries.map(country => (
                                    <MenuItem key={country.countryCode} value={country.countryCode}>
                                        {country.countryName.es}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth label="Año de Inicio" value={startYear} onChange={(e) => setStartYear(e.target.value)} />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                        <TextField fullWidth label="Año Final" value={endYear} onChange={(e) => setEndYear(e.target.value)} />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <Button fullWidth variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading} sx={{ height: '56px' }}>
                            Buscar
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateModal}>Añadir Festivo</Button>
            </Box>

            {loading && <CircularProgress sx={{ display: 'block', margin: 'auto' }} />}
            {error && <Typography color="error" align="center">{error}</Typography>}

            {!loading && holidays.length > 0 && (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Fecha</TableCell>
                                <TableCell>Nombre (ES)</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell align="right">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {holidays.map((holiday) => (
                                <TableRow hover key={`${holiday.countryYear}-${holiday.date}`}>
                                    <TableCell>{holiday.date}</TableCell>
                                    <TableCell>{holiday.name.es}</TableCell>
                                    <TableCell>{holiday.type}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => openEditModal(holiday)}><EditIcon /></IconButton>
                                        <IconButton size="small" onClick={() => handleDelete(holiday)}><DeleteIcon color="error" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
                <Box sx={modalStyle}>
                    <Typography variant="h6" component="h2">{isEditing ? 'Editar Festivo' : 'Crear Nuevo Festivo'}</Typography>
                    <Grid container spacing={2} sx={{ mt: 2 }}>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>País</InputLabel>
                                <Select
                                    name="countryCode"
                                    value={currentHoliday.countryCode}
                                    label="País"
                                    onChange={handleFormChange}
                                    disabled={isEditing} // No se puede cambiar el país al editar
                                >

                                    <MenuItem value="" disabled>
                                        <em>Selecciona un país</em>
                                    </MenuItem>

                                    {countries.map(country => (
                                        <MenuItem key={country.countryCode} value={country.countryCode}>
                                            {country.countryName.es}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField fullWidth label="Fecha (YYYY-MM-DD)" name="date" value={currentHoliday.date} onChange={handleFormChange} disabled={isEditing} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Nombre en Español" name="name_es" value={currentHoliday.name_es} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Nombre en Inglés" name="name_en" value={currentHoliday.name_en} onChange={handleFormChange} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Tipo</InputLabel>
                                <Select name="type" value={currentHoliday.type} label="Tipo" onChange={handleFormChange}>
                                    <MenuItem value={'RELIGIOUS'}>Religioso</MenuItem>
                                    <MenuItem value={'CIVIC'}>Cívico</MenuItem>
                                    <MenuItem value={'OTHER'}>Otro</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
                            <Button onClick={() => setIsModalOpen(false)}>Cancelar</Button>
                            <Button variant="contained" onClick={handleSave}>Guardar</Button>
                        </Grid>
                    </Grid>
                </Box>
            </Modal>
        </Box>
    );
};

export default HolidayManagement;